import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
export interface Channel {
  hash_id: string;
  title: string;
  username: string | null;
  subscribers: number;
  entity_type: string; // 'channel' | 'group'
  photo_path?: string | null;
  photo_url?: string | null;
  last_message_id: number;
  last_check_time: string;
  message_count: number;
}

export interface Message {
  id: number;
  channel_hash_id: string;
  message_id: number;
  text: string;
  date: string;
  sender_id: number | null;
  views: number;
  forwards: number;
  replies: number;
  entity_type: string; // 'channel' | 'group'
  created_at: string;
}

export interface TelegramStats {
  total_channels: number;
  total_messages: number;
  messages_last_hour: number;
  messages_last_24h: number;
  most_active_channel: string | null;
}

export interface SearchParams {
  channel_hash?: string;
  keywords?: string[];
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

export interface SearchResponse {
  messages: Message[];
  total: number;
  page: number;
  page_size: number;
}
export const telegramApi = createApi({
  reducerPath: 'telegramApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_BASE_URL}/telegram`,
    headers: {
      Authorization: `Bearer ${import.meta.env.VITE_TELEGRAM_API_ACCESS_TOKEN}`,
      'Content-Type': 'application/json',
    },
  }),
  tagTypes: ['Channels', 'Messages', 'Stats'],
  endpoints: (builder) => ({
    getChannels: builder.query<Channel[], void>({
      query: () => '/channels',
      providesTags: ['Channels'],
    }),

    getStats: builder.query<TelegramStats, void>({
      query: () => '/stats',
      providesTags: ['Stats'],
    }),

    getMessages: builder.query<Message[], { limit?: number; offset?: number }>({
      query: ({ limit = 50, offset = 0 }) => 
        `/messages?limit=${limit}&offset=${offset}`,
      providesTags: ['Messages'],
    }),

    getMessagesByChannel: builder.query<
      Message[],
      { channelHash: string; limit?: number; offset?: number }
    >({
      query: ({ channelHash, limit = 50, offset = 0 }) =>
        `/messages/${channelHash}?limit=${limit}&offset=${offset}`,
      providesTags: (_result, _error, { channelHash }) => [
        { type: 'Messages', id: channelHash },
      ],
    }),

    searchMessages: builder.mutation<SearchResponse, SearchParams>({
      query: (params) => ({
        url: '/search',
        method: 'POST',
        body: params,
      }),
    }),

    getLatestMessages: builder.query<Message[], number>({
      query: (limit = 10) => `/latest?limit=${limit}`,
      providesTags: ['Messages'],
    }),
  }),
});

// Export hooks
export const {
  useGetChannelsQuery,
  useGetStatsQuery,
  useGetMessagesQuery,
  useGetMessagesByChannelQuery,
  useLazyGetMessagesByChannelQuery,
  useSearchMessagesMutation,
  useGetLatestMessagesQuery,
} = telegramApi;